import { useT } from "../lib/i18n.jsx";
import { useToast } from "../components/Toast.jsx";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, getToken } from "../lib/api.js";
import { IconArrow, IconMonitor } from "../components/icons.jsx";

const PLATFORM_LABELS = {
  windows: "Windows",
  linux: "Linux",
  macos: "macOS",
};

function fmtSize(bytes) {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  return mb < 1 ? `${(bytes / 1024).toFixed(0)} KB` : `${mb.toFixed(1)} MB`;
}

export default function DownloadsPage() {
  const { t } = useT();
  const toast = useToast();
  const [releases, setReleases] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api("/downloads/list")
      .then((r) => setReleases(r.releases))
      .catch((err) => setError(err.message));
  }, []);

  if (!releases && !error) return <div className="loading">Loading downloads</div>;

  // Group by platform
  const byPlatform = {};
  for (const r of releases || []) {
    if (!byPlatform[r.platform]) byPlatform[r.platform] = [];
    byPlatform[r.platform].push(r);
  }

  return (
    <>
      <div className="ph">
        <div className="ph-title">
          <div className="eyebrow">Get the app</div>
          <h1 className="h1">Download Vorks Connect</h1>
        </div>
        <div className="ph-meta">
          Install on any device <br />you want to access or share
        </div>
      </div>

      {error && <div className="error-line" style={{ marginBottom: 18 }}>{error}</div>}

      {releases && releases.length === 0 && (
        <div className="empty">
          <div className="empty-title">no releases available yet</div>
          <div className="empty-body">
            an admin needs to upload installer binaries first
          </div>
        </div>
      )}

      <div className="grid">
        {["windows", "linux", "macos"].map((platform) => {
          const items = byPlatform[platform] || [];
          if (items.length === 0) return null;
          return items.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.4 }}
              className="card"
              style={{ padding: 28 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div className="eyebrow">{PLATFORM_LABELS[r.platform] || r.platform}</div>
                  <div className="card-name" style={{ fontSize: 24, marginTop: 6 }}>
                    Vorks Connect
                  </div>
                  <div className="mono dim" style={{ fontSize: 11, marginTop: 4 }}>
                    v{r.version} · {r.arch}
                  </div>
                </div>
                <IconMonitor size={24} className="dim" />
              </div>

              {r.notes && (
                <p style={{ fontSize: 13, color: "var(--text-mute)", margin: 0 }}>
                  {r.notes}
                </p>
              )}

              <div className="meta-line">
                <span>{fmtSize(r.size_bytes)}</span>
                <span>{r.filename}</span>
              </div>

              <DownloadButton release={r} />

              <details style={{ marginTop: 8 }}>
                <summary style={{
                  cursor: "pointer",
                  fontFamily: "var(--mono)",
                  fontSize: 10,
                  color: "var(--text-faint)",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}>
                  Verify checksum
                </summary>
                <div className="mono" style={{
                  fontSize: 10,
                  color: "var(--text-dim)",
                  marginTop: 8,
                  wordBreak: "break-all",
                  padding: "8px 10px",
                  background: "var(--bg)",
                  border: "1px solid var(--line)",
                  borderRadius: "var(--r-sm)",
                }}>
                  SHA-256: {r.sha256}
                </div>
              </details>
            </motion.div>
          ));
        })}
      </div>

      <div className="section" style={{ marginTop: 32 }}>
        <div className="section-head">
          <h2>After installing</h2>
        </div>
        <div className="section-body">
          <ol style={{ paddingLeft: 18, lineHeight: 1.8, fontSize: 14, color: "var(--text-mute)" }}>
            <li>Launch <strong style={{ color: "var(--text)" }}>Vorks Connect</strong> from your start menu / applications.</li>
            <li>Enter the backend URL: <span className="mono" style={{ color: "var(--acc)" }}>{window.location.origin}</span></li>
            <li>Click <strong style={{ color: "var(--text)" }}>"Sign in with browser"</strong>. Your browser will open this site and ask you to approve the device.</li>
            <li>Choose whether you want this machine to act as a host, viewer, or both.</li>
          </ol>
        </div>
      </div>
    </>
  );
}

function DownloadButton({ release }) {
  const [busy, setBusy] = useState(false);

  async function download() {
    setBusy(true);
    try {
      // Fetch with auth header, then save as file
      const url = `/api/downloads/file/${release.platform}/${release.arch}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = release.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      toast.error("Download failed: " + err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      className="btn primary full lg"
      onClick={download}
      disabled={busy}
      style={{ marginTop: "auto" }}
    >
      {busy ? "Preparing…" : "Download installer"} <IconArrow size={14} />
    </button>
  );
}
